/**
 * Navigation model - one definition drives the desktop sidebar, the mobile
 * bottom bar and the command palette, and every entry is permission-gated so a
 * role never sees a screen it cannot open.
 */
import {
  BarChart3, Boxes, CalendarDays, ChefHat, ClipboardList, CreditCard, FileText,
  Gauge, LayoutGrid, ScrollText, Settings, ShoppingCart, ShoppingBasket,
  Users, UtensilsCrossed, Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  permission: string;
  /** Phase in which this screen is built. Locked items show a "coming soon" hint. */
  phase: number;
  /** Shown in the mobile bottom bar (max 5 across all roles). */
  primary?: boolean;
  section: 'Overview' | 'Operations' | 'Supply' | 'People' | 'Insights' | 'Administration';
}

export const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: Gauge, permission: 'dashboard:view', phase: 1, primary: true, section: 'Overview' },
  { to: '/pos', label: 'POS', icon: LayoutGrid, permission: 'pos:use', phase: 4, primary: true, section: 'Operations' },
  { to: '/orders', label: 'Orders', icon: ClipboardList, permission: 'orders:view', phase: 4, section: 'Operations' },
  { to: '/kds', label: 'Kitchen', icon: ChefHat, permission: 'kds:view', phase: 4, primary: true, section: 'Operations' },
  { to: '/billing', label: 'Billing', icon: CreditCard, permission: 'pos:take_payment', phase: 5, section: 'Operations' },
  { to: '/menu', label: 'Menu', icon: UtensilsCrossed, permission: 'menu:view', phase: 2, section: 'Operations' },
  { to: '/recipes', label: 'Recipes', icon: ShoppingBasket, permission: 'recipes:view', phase: 2, section: 'Supply' },
  { to: '/inventory', label: 'Inventory', icon: Boxes, permission: 'inventory:view', phase: 2, primary: true, section: 'Supply' },
  { to: '/purchase', label: 'Purchase', icon: ShoppingCart, permission: 'purchase:view', phase: 3, section: 'Supply' },
  { to: '/crm', label: 'Customers', icon: Users, permission: 'crm:view', phase: 7, section: 'People' },
  { to: '/reservations', label: 'Reservations', icon: CalendarDays, permission: 'crm:view', phase: 7, section: 'People' },
  { to: '/staff', label: 'Staff', icon: Users, permission: 'staff:view', phase: 7, section: 'People' },
  { to: '/expenses', label: 'Expenses', icon: Wallet, permission: 'expenses:view', phase: 7, section: 'Insights' },
  { to: '/reports', label: 'Reports', icon: BarChart3, permission: 'reports:sales', phase: 6, section: 'Insights' },
  { to: '/users', label: 'Users', icon: Users, permission: 'settings:users', phase: 1, section: 'Administration' },
  { to: '/roles', label: 'Roles & access', icon: FileText, permission: 'settings:view', phase: 1, section: 'Administration' },
  { to: '/audit', label: 'Audit log', icon: ScrollText, permission: 'audit:view', phase: 1, section: 'Administration' },
  { to: '/settings', label: 'Settings', icon: Settings, permission: 'settings:view', phase: 1, primary: true, section: 'Administration' },
];

export const PHASE_TITLES: Record<number, string> = {
  1: 'Phase 1 · Database & access control',
  2: 'Phase 2 · Menu, recipes & inventory',
  3: 'Phase 3 · Purchase management',
  4: 'Phase 4 · POS, orders & kitchen',
  5: 'Phase 5 · Billing & split payment',
  6: 'Phase 6 · Reports & dashboard analytics',
  7: 'Phase 7 · CRM, staff, expenses & settings',
};

/** Items a user can see, grouped for the sidebar. */
export function navFor(permissions: string[]) {
  const allowed = NAV.filter((n) => permissions.includes(n.permission));
  const order = ['Overview', 'Operations', 'Supply', 'People', 'Insights', 'Administration'] as const;
  return order
    .map((section) => ({ section, items: allowed.filter((n) => n.section === section) }))
    .filter((g) => g.items.length > 0);
}

/** The five most relevant entries for the mobile bottom bar. */
export function bottomNavFor(permissions: string[]) {
  const primary = NAV.filter((n) => n.primary && permissions.includes(n.permission));
  if (primary.length >= 5) return primary.slice(0, 5);
  // Top up with anything else the user can reach so the bar is never sparse.
  const rest = NAV.filter((n) => !n.primary && permissions.includes(n.permission));
  return [...primary, ...rest].slice(0, 5);
}
